import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./Products.css";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

const Products = () => {
  const { category, subcategory } = useParams();
  const navigate = useNavigate();
  useScrollAnimation();

  const [activeFilter, setActiveFilter] = useState(subcategory || "all");
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(9);

  // Enhanced product data with subcategories
  const categories = [
    {
      id: "fresh-produce",
      name: "Fresh Produce",
      description:
        "Premium fruits and vegetables from Kenya's fertile highlands",
      image: "../../../assets/images/categories/fresh-products.png",
      subcategories: [
        { id: "vegetables", name: "Vegetables" },
        { id: "fruits", name: "Fruits" },
        { id: "herbs", name: "Herbs & Spices" },
      ],
      products: [
        {
          id: 1,
          name: "Avocados (Hass & Fuerte)",
          description:
            "Premium export-quality avocados with perfect ripeness and flavor.",
          image: "../../../assets/images/products/avocado-prd.png",
          category: "fruits",
          subcategory: "fruits",
        },
        {
          id: 2,
          name: "Mangoes (Kent & Apple)",
          description:
            "Sweet, juicy mangoes harvested at peak season for optimal taste.",
          image: "../../../assets/images/products/mangoes-prd.png",
          category: "fruits",
          subcategory: "fruits",
        },
        {
          id: 3,
          name: "French Beans",
          description:
            "Tender, crisp French beans grown in the highlands of Kenya.",
          image: "../../../assets/images/products/french-beans-prd.png",
          category: "vegetables",
          subcategory: "vegetables",
        },
        {
          id: 4,
          name: "Snow Peas",
          description:
            "Sweet and crunchy snow peas, carefully selected for export.",
          image: "../../../assets/images/products/snow-peas-prd.JPG",
          category: "vegetables",
          subcategory: "vegetables",
        },
        {
          id: 13,
          name: "Kale",
          description: "Fresh, nutrient-rich kale from organic farms.",
          image: "../../../assets/images/products/kale-prd.png",
          category: "vegetables",
          subcategory: "vegetables",
        },
        {
          id: 14,
          name: "Spinach",
          description: "Tender spinach leaves, perfect for salads and cooking.",
          image: "../../../assets/images/products/spinach-prd.jpg",
          category: "vegetables",
          subcategory: "vegetables",
        },
        {
          id: 15,
          name: "Broccoli",
          description:
            "Fresh broccoli with tight florets and vibrant green color.",
          image: "../../../assets/images/products/broccoli-prd.png",
          category: "vegetables",
          subcategory: "vegetables",
        },
        {
          id: 16,
          name: "Pineapples",
          description: "Sweet, golden pineapples with perfect sugar content.",
          image: "../../../assets/images/products/pineapple-prd.jpg",
          category: "fruits",
          subcategory: "fruits",
        },
        {
          id: 17,
          name: "Passion Fruit",
          description: "Aromatic passion fruit with intense flavor.",
          image: "../../../assets/images/products/passion-fruit-prd.png",
          category: "fruits",
          subcategory: "fruits",
        },
        {
          id: 18,
          name: "Basil",
          description: "Fresh aromatic basil for culinary use.",
          image: "../../../assets/images/products/basil-prd.png",
          category: "herbs",
          subcategory: "herbs",
        },
        {
          id: 19,
          name: "Coriander (Cilantro)",
          description: "Fresh cilantro with vibrant flavor.",
          image: "../../../assets/images/products/cilantro-prd.jpg",
          category: "herbs",
          subcategory: "herbs",
        },
        {
          id: 20,
          name: "Rosemary",
          description: "Aromatic rosemary for cooking and garnishing.",
          image: "../../../assets/images/products/rosemary-prd.png",
          category: "herbs",
          subcategory: "herbs",
        },
      ],
    },
    {
      id: "tea",
      name: "Tea",
      description:
        "World-renowned tea leaves from Kenya's high-altitude plantations",
      image: "../../../assets/images/categories/tea-products.png",
      subcategories: [
        { id: "black-tea", name: "Black Tea" },
        { id: "green-tea", name: "Green Tea" },
        { id: "herbal-tea", name: "Herbal Tea" },
      ],
      products: [
        {
          id: 5,
          name: "Black CTC Tea",
          description:
            "Rich, full-bodied black tea with robust flavor and aroma.",
          image: "../../../assets/images/products/black-tea-prd.JPG",
          category: "black-tea",
          subcategory: "black-tea",
        },
        {
          id: 6,
          name: "Purple Tea",
          description:
            "Antioxidant-rich purple tea, a unique Kenyan specialty.",
          image: "../../../assets/images/products/purple-tea-prd.JPEG",
          category: "black-tea",
          subcategory: "black-tea",
        },
        {
          id: 7,
          name: "Green Tea",
          description:
            "Delicate green tea with fresh, vegetal notes and health benefits.",
          image: "../../../assets/images/products/green-tea-prd.png",
          category: "green-tea",
          subcategory: "green-tea",
        },
        {
          id: 8,
          name: "Herbal Tea Blends",
          description: "Custom herbal tea blends featuring Kenyan ingredients.",
          image: "../../../assets/images/products/herbal-tea-prd.JPG",
          category: "herbal-tea",
          subcategory: "herbal-tea",
        },
      ],
    },
    {
      id: "coffee",
      name: "Coffee",
      description:
        "Premium Arabica coffee beans from the slopes of Mount Kenya",
      image: "../../../assets/images/categories/coffee-products.png",
      subcategories: [
        { id: "arabica", name: "Arabica" },
        { id: "specialty", name: "Specialty Blends" },
        { id: "premium", name: "Premium Grade" },
      ],
      products: [
        {
          id: 9,
          name: "AA Grade Coffee",
          description:
            "The largest bean size with exceptional flavor complexity.",
          image: "../../../assets/images/products/aa-coffee.prd.png",
          category: "premium",
          subcategory: "premium",
        },
        {
          id: 10,
          name: "AB Grade Coffee",
          description: "Medium bean size with balanced acidity and body.",
          image: "../../../assets/images/products/ab-coffee-prd.png",
          category: "arabica",
          subcategory: "arabica",
        },
        {
          id: 11,
          name: "PB Grade Coffee",
          description: "Peaberry beans with concentrated flavor and sweetness.",
          image: "../../../assets/images/products/pb-coffee-prd.png",
          category: "specialty",
          subcategory: "specialty",
        },
        {
          id: 12,
          name: "Specialty Blends",
          description: "Custom coffee blends for discerning clients worldwide.",
          image:
            "../../../assets/images/products/coffee-specialty-blend-prd.png",
          category: "specialty",
          subcategory: "specialty",
        },
      ],
    },
    {
      id: "nuts-others",
      name: "Nuts & Others",
      description:
        "Premium quality nuts and other specialty products from Kenya",
      image: "../../../assets/images/categories/commodities-products.jpg",
      subcategories: [
        { id: "macadamia", name: "Macadamia Nuts" },
        { id: "cashewnuts", name: "Cashew Nuts" },
      ],
      products: [
        {
          id: 21,
          name: "Macadamia Nuts",
          description:
            "Premium grade macadamia nuts with rich, buttery flavor.",
          image: "../../../assets/images/products/macadamia-prd.jpg",
          category: "nuts",
          subcategory: "macadamia",
        },
        {
          id: 22,
          name: "Cashew Nuts",
          description:
            "Premium quality cashew nuts with delicate, sweet flavor.",
          image: "../../../assets/images/products/cashew-prd.png",
          category: "nuts",
          subcategory: "cashewnuts",
        },
      ],
    },
  ];

  // Get current category
  const currentCategory = categories.find((cat) => cat.id === category);

  // Filter products based on active filter
  const filteredProducts =
    activeFilter === "all"
      ? currentCategory?.products || []
      : currentCategory?.products.filter(
          (product) => product.subcategory === activeFilter
        ) || [];

  // Pagination logic
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle filter change
  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    setCurrentPage(1);
    navigate(`/products/${category}/${filterId}`);
  };

  // Handle products per page change
  const handleProductsPerPageChange = (e) => {
    setProductsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Add animation delays on component mount
  useEffect(() => {
    const animateElements = document.querySelectorAll(".animate-on-load");
    animateElements.forEach((element, index) => {
      element.style.animationDelay = `${0.2 + index * 0.1}s`;
    });

    // Reset to first page when category changes
    setCurrentPage(1);
  }, [category, subcategory]);

  if (!currentCategory) {
    return (
      <>
        <Header />
        <div className="products-page">
          <div className="container">
            <div className="not-found animate-on-scroll">
              <h2>Category Not Found</h2>
              <p>The requested category does not exist.</p>
              <button className="btn btn-primary" onClick={() => navigate("/")}>
                Go Back Home
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="products-page">
        {/* Hero Section */}
        <section className="products-hero">
          <div className="hero-background">
            <div className="parallax-img products-img-1"></div>
            <div className="overlay"></div>
          </div>
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title animate-on-load">
                {currentCategory.name}
              </h1>
              <p className="hero-subtitle animate-on-load">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section className="products-section">
          <div className="container">
            {/* Subcategory Filter */}
            <div className="subcategory-filter animate-on-scroll">
              <button
                className={`filter-btn ${
                  activeFilter === "all" ? "active" : ""
                }`}
                onClick={() => handleFilterChange("all")}
              >
                All {currentCategory.name}
              </button>
              {currentCategory.subcategories.map((subcat) => (
                <button
                  key={subcat.id}
                  className={`filter-btn ${
                    activeFilter === subcat.id ? "active" : ""
                  }`}
                  onClick={() => handleFilterChange(subcat.id)}
                >
                  {subcat.name}
                </button>
              ))}
            </div>

            {/* Results Info */}
            <div className="results-info animate-on-scroll delay-1">
              <p>
                Showing {filteredProducts.length} product
                {filteredProducts.length !== 1 ? "s" : ""}
                {activeFilter !== "all" &&
                  ` in ${
                    currentCategory.subcategories.find(
                      (sc) => sc.id === activeFilter
                    )?.name
                  }`}
              </p>
              <div className="products-per-page">
                <label htmlFor="productsPerPage">Show:</label>
                <select
                  id="productsPerPage"
                  value={productsPerPage}
                  onChange={handleProductsPerPageChange}
                >
                  <option value={6}>6</option>
                  <option value={9}>9</option>
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                </select>
              </div>
            </div>

            {/* Products Grid */}
            <div className="products-grid">
              {currentProducts.length > 0 ? (
                currentProducts.map((product, index) => (
                  <div
                    key={product.id}
                    className={`product-card
                    `}
                  >
                    <div className="product-image">
                      <img
                        src={product.image}
                        alt={product.name}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <div className="image-placeholder">
                        <span>{product.name.split(" ")[0]}</span>
                      </div>
                      <div className="product-overlay">
                        <button className="btn-quick-view">Quick View</button>
                      </div>
                    </div>
                    <div className="product-content">
                      <h3>{product.name}</h3>
                      <p>{product.description}</p>
                      <div className="product-actions">
                        <button
                          className="btn-learn-more"
                          onClick={() => navigate(`/product/${product.id}`)}
                        >
                          Learn More
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M5 12H19M19 12L12 5M19 12L12 19"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <button className="btn-inquire">Inquire Now</button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-products animate-on-scroll">
                  <p>No products found in this category.</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination animate-on-scroll">
                <button
                  className={`pagination-btn ${
                    currentPage === 1 ? "disabled" : ""
                  }`}
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      className={`pagination-btn ${
                        currentPage === page ? "active" : ""
                      }`}
                      onClick={() => paginate(page)}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  className={`pagination-btn ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Related Categories */}
        <section className="related-categories">
          <div className="container">
            <div className="section-header animate-on-scroll">
              <h2>Explore Other Categories</h2>
              <p>Discover more premium Kenyan agricultural products</p>
            </div>
            <div className="categories-grid">
              {categories
                .filter((cat) => cat.id !== category)
                .map((cat, index) => (
                  <div
                    key={cat.id}
                    className={`category-card animate-on-scroll delay-${index}`}
                  >
                    <div className="category-image">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <div className="image-placeholder large">
                        <span>{cat.name}</span>
                      </div>
                      <div className="category-overlay"></div>
                    </div>
                    <div className="category-content">
                      <h3>{cat.name}</h3>
                      <p>{cat.description}</p>
                      <button
                        className="btn-explore-category"
                        onClick={() => navigate(`/products/${cat.id}/all`)}
                      >
                        Explore {cat.name}
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M5 12H19M19 12L12 5M19 12L12 19"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <div className="products-cta animate-on-scroll">
          <div className="container">
            <div className="cta-content">
              <h3 className="animate-right">
                Need custom products or bulk orders?
              </h3>
              <p className="animate-left">
                Contact us for customized solutions and wholesale pricing
              </p>
              <div className="cta-buttons animate-on-scroll delay-1">
                <button className="btn btn-primary">
                  Request Custom Quote
                </button>
                <button className="btn btn-secondary">Contact Sales</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Products;
